import React, { memo, useRef } from 'react';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useRouteMatch } from 'react-router-dom';
import { Feature, FeatureCollection } from '@turf/helpers';
import { Alert, Button, Col, Form, ListGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as turf from '@turf/turf';
import { useMutation, useQuery } from 'react-apollo';
import { CreateGrower } from '../CreateGrower';
import { CreateFarm } from '../CreateFarm';
import { calculateAcres, INTERNAL_FIELD_ID, toMultiPolygonGeom } from '../../../../utils/mapUtils';
import { Crop, Farm, Field, Grower, Variety  } from '../../../../types/types';
import { useStore } from '../../../../providers/AppProvider';
import * as yup from 'yup';
import { GetFarms, GetGrowers, landscan_fields_insert_input } from '../../../../../../__generated__/types';
import { CREATE_FIELDS, CREATE_FIELD, GET_FARMS, GET_GROWERS2, UPDATE_FIELD } from '../../../../../../queries';

const FIELD_APP_URL = process.env.REACT_APP_FIELD_MGR_ROOT_URL;

type ImportFormProps = {
    features: FeatureCollection | null,
    children: React.ReactNode,
    onSelectField: Function,
    onHoverField: Function
};

interface IMappings {
    name: string,
    year_planted: string,
    plant_spacing: string,
    row_spacing: string,
    crop: string,
    variety: string,
    acres: string,
    cached_shp_acres: string
}

type FieldInput = {
    id: string,
    name: string,
    year_planted: string,
    plant_spacing: string,
    row_spacing: string,
    crop_id: string,
    variety: string,
    acres: string,
    cached_shp_acres: string,
    grower_id: string,
    farm_id: string,
};

const defaultMappings = {
    name: '',
    year_planted: '',
    plant_spacing: '',
    row_spacing: '',
    crop: '',
    variety: '',
    acres: '',
    cached_shp_acres: ''
};

/*const schema = yup.object().shape({
    name: yup.string().required().max(255),
    year_planted: yup.number(),
    acres: yup.number().positive('a valid block size is required'),
    grower_id: yup.number(),
    farm_id: yup.number(),
    crop_id: yup.number(),//.required('crop is required'),
    variety_id: yup.number(),
    plant_spacing: yup.number().positive('a valid plant spacing is required'),
    row_spacing: yup.number().positive('a valid row spacing is required'),
    url: yup.string().url().max(512)
});*/

type FieldFormValues = {
    grower_id: string,
    farm_id: string,
    name: string,
    crop_id: string,
    variety_id: string,
    year_planted: string,
    acres: string,
    plant_spacing: string,
    row_spacing: string,
    url: string
};

const ImportForm = ({ children, features, onHoverField, onSelectField }: ImportFormProps) => {  
    const match = useRouteMatch(`${FIELD_APP_URL}/:vp/fields`);
    const history = useHistory();

    const ref = useRef<any>(null);

    const { state: { crops, varieties }, orgId } = useStore();
    const [featureCollection, setFeatureCollection] = useState<FeatureCollection | null>(null);
    const [fieldInputs, setFieldInputs] = useState<FieldInput[]>([]);

    const [currentGrowerFarms, setCurrentGrowerFarms] = useState<Farm[]>([]);
    const [growers, setGrowers] = useState<Grower[]>([]);
    const [currentGrowerId, setCurrentGrowerId] = useState<number | null>(null);
    const [currentGrowerFarm, setCurrentGrowerFarm] = useState<number | null>(null);
    const [farms, setFarms] = useState<Farm[]>([]);

    const [mapping, setMapping] = useState<IMappings>(defaultMappings);
    const [createFields] = useMutation(CREATE_FIELDS, {
        awaitRefetchQueries: true,
        refetchQueries: [
            'GetFields' // Query name
        ],
        onCompleted: () => { 
            history.push(`${match.url}`);
        }
    });
    const [defaultValues, setDefaultValues] = useState<FieldFormValues>({
        grower_id: "",
        farm_id: "",
        name: "",
        crop_id: "",
        variety_id: "",
        year_planted: "",
        acres: "",
        plant_spacing: "",
        row_spacing: "",
        url: ""
    });

    const [showCreateGrower, setShowCreateGrower] = useState<boolean>(false);
    const [showCreateFarm, setShowCreateFarm] = useState<boolean>(false);

    const { data: growerData } = useQuery<GetGrowers>(GET_GROWERS2);
    const { data: farmData } = useQuery<GetFarms>(GET_FARMS);

    const setGrowersFarms = (growerId: number) => {
        console.log("072224 growerId", growerId);
        console.log("072224 farms", farms);
        setCurrentGrowerFarms(growerId > 0 ? farms.filter((f: Farm) => f.grower_id === growerId) : [])
    }
    
    useEffect(() => {
        setGrowers(growerData ? growerData.landscan_growers.map(g => { 
            return {
                id: g.grower_id,
                name: g.name,
                description: g.description
            } as Grower
        }) : []);
    }, [growerData]);

    useEffect(() => {
        setFarms(farmData ? farmData.landscan_farms.map(f => { 
            return {
                id: f.farm_id,
                grower_id: f.grower_id,
                name: f.name,
                description: f.description
            } as Farm
        }) : []);
    }, [farmData]);

    useEffect(() => {
        // force to update the list of the current grower
        // when the data updates.
        console.log("072224 currentGrowerId", currentGrowerId);
        console.log("072224 farms", farms);
        if(currentGrowerId) {
            setGrowersFarms(currentGrowerId);
        }
    }, [farms]);

    useEffect(() => {
        setFeatureCollection(features);
    }, [features]);

    useEffect(() => {
        console.log("072224 currentGrowerFarms", currentGrowerFarms);
    }, [currentGrowerFarms]);

    useEffect(() => {
        setFieldInputs(featureCollection ? createInputs(featureCollection) : []);
        setMapping({ ...defaultMappings });
    }, [featureCollection]);

    const schema = yup.object().shape({
        inputs: yup.array().of(
            yup.object().shape({
                name: yup.string().max(255),//.required('Field name is required').max(255),
                crop_id: yup.string(),//.required('Crop type is required'),
                year_planted: yup.number()
                    .min(1990, 'Year planted must be greater than 1990')
                    .typeError( 'Year planted is set to a non-numeric value'),
                plant_spacing: yup.number().typeError( 'Plant spacing is set to a non-numeric value'),
                row_spacing: yup.number().typeError( 'Row spacing is set to a non-numeric value'),
                id: yup.string().test('valid-geom', 'Geometry is not valid.', value => {
                    const feature:any = featureCollection!.features.find((f: Feature) => f.id == value);
                    const featureCol = turf.kinks(feature);
                    return featureCol.features.length === 0;
                }),
            })
        )
    });

    const getValue = (feature: Feature, key: string): string => {
        if (!feature || !key.length) {
            return '';
        }

        return feature.properties && feature.properties.hasOwnProperty(key) ? feature.properties[key] : '';
    }

    const updateInputsValue = (key: string, value:string) => {
        console.log("C071924 key", key);
        console.log("C071924 value", value);
        if (key === "crop"){
            key = "crop_id";
        }

        if (!featureCollection ) {
            return;
        }

        // the mapping config has changed. mapping object defines the mapping from the properties
        // in the the feature properties list to the value in the field form. for example, what value
        // in the feature property list should be used for the field name. 

        // make a copy so the current values are maintained. only the column that has been
        // changed in the mapping will get updated.
        if(ref && ref.current) {
            let inputs = JSON.parse(JSON.stringify(ref.current.values.inputs))
            inputs.forEach((input: FieldInput) => {

                console.log("C071924 input", input);
                if (key === "acres" && value === "-99") {
                    const feature = featureCollection.features.find((f: Feature) => f.id == input.id);
                    input[key as keyof IMappings] = input.cached_shp_acres;
                    return;
                }
                const feature = featureCollection.features.find((f: Feature) => f.id == input.id);
                if (feature) {
                    input[key as keyof IMappings] = getValue(feature, value).toString();
                }
            });

            setFieldInputs(inputs);
        }
    }

    const getMappingOptions = (feature: Feature): string[] => {
        if (!feature || !feature.properties) {
            return [];
        }

        return Object.keys(feature.properties).filter((k: string) => k !== INTERNAL_FIELD_ID)
    }

    const onMappingChanged = (e: any) => {
        console.log("C071924 e.target.name", e.target.name);
        console.log("C071924 e.target.value", e.target.value);
        setMapping({
            ...mapping, [e.target.name]: e.target.value
        });

        updateInputsValue(e.target.name, e.target.value);
    }

    const createInputs = (collection: FeatureCollection): FieldInput[] => {
        const inputs = collection ? collection.features.map((f: Feature) => {
            const area = turf.area(f.geometry as any); // Calculate area in square meters
            const acres = area * 0.000247105; // Convert square meters to acres
            return {
                id: f.id as string,
                name: '',
                crop_id: '',
                year_planted: '',
                plant_spacing: '',
                row_spacing: '',
                variety:'',
                acres: acres.toFixed(3).toString(), // Round to 2 decimal places
                cached_shp_acres: acres.toFixed(3).toString()
            } as FieldInput;
        }) : [];

        return inputs.sort((a: FieldInput, b: FieldInput) => a.id < b.id ? -1 : 1);
    }

    const onMouseEnter = (input: FieldInput) => {
        onHoverField(input.id);
    }

    const onMouseLeave = (input: FieldInput) => {
        onHoverField(null);
    }

    const hasError = (errors: any, index: number, prop: string): boolean => {
        if (!errors && !errors.inputs) {
            return false;
        }

        if (errors.inputs && errors.inputs.length && errors.inputs[index]) {
            return errors.inputs[index].hasOwnProperty(prop);
        }

        return false;
    }

    const hasAnyErrors = (errors: any, index: number): boolean => {
        if (!errors && !errors.inputs) {
            return false;
        }

        return errors.inputs && errors.inputs.length && errors.inputs[index];
    }

    const errorMessage = (errors: any, index: number, prop: string): string => {
        if (!errors && !errors.inputs) {
            return '';
        }

        const {inputs} = errors;

        if (inputs && inputs.length && inputs[index] && inputs[index].hasOwnProperty(prop)) {
            return inputs[index][prop];
        }

        return '';
    }

     const doFieldImport = async (formValues: FieldInput[]) => {
        if(!featureCollection) {
            return;
        }

        // following field values are either entered by the user or mapped from the input source
        // name, year planted, row and plant spacing
        const fieldsA = formValues.filter((data: FieldInput) => data.name != "" && data.name != undefined && data.name != null);
        const fields = fieldsA.map((data: FieldInput) => {
            const feature = featureCollection.features.find((f: Feature) => f.id == data.id);
            const cropId = crops.find((f: any) => f.name.toLowerCase() == data.crop_id.toLowerCase());
            let varietyId = varieties.find((f: any) => f.name.toLowerCase() == data.variety.toLowerCase());
            console.log("A072224 varieties", varieties);
            if (!varietyId) {
                varietyId = varieties.find((f: any) => f.id.toString().toLowerCase() == data.variety.toLowerCase());
            }
            console.log("A072224 varietyId", varietyId);
            const farm = currentGrowerFarm; //currentGrowerFarms.find((f: Farm) => f.name == data.farm_id );
            const grower = currentGrowerId; //growers.find((f: Grower) => f.name == data.grower_id );
            const area = calculateAcres(feature!);
            console.log("A072224 data", data);
            console.log("A072224 farm", farm);
            console.log("A072224 grower", grower);
            const field: landscan_fields_insert_input = {
                org_id: orgId,
                field_id: undefined,
                name: data.name,
                crop_id: cropId ? cropId.id : null,
                acres: data.acres != ""  ? data.acres : area.toFixed(2),
                plant_spacing: data.plant_spacing != "" ? parseFloat(data.plant_spacing) : null,
                row_spacing: data.row_spacing != "" ? parseFloat(data.row_spacing) : null,
                year_planted: data.year_planted != "" ? parseInt(data.year_planted) : null,
                geom: JSON.stringify(toMultiPolygonGeom(feature!)), 
                farm_id: farm ? farm : null,
                grower_id: grower ? grower : null,
                variety_id: varietyId ? varietyId.id : null,
            };

            return field;
        });     
        
        console.log("A072224 currentGrowerId", currentGrowerId);
        console.log("A072224 formValues", formValues);
        console.log("A072224 fields ----> ", fields);
        await createFields({ variables: {data: fields} } );
     }

    return (
        <div className="d-flex flex-column h-100 overflow-auto">
            <div className="d-flex justify-content-between px-3 py-2">
                <h4>Import Block Boundaries</h4>
            </div>

            <div>
                {features && features.features.length > 0 &&
                    <div className="px-3 py-2">
                        <h5>Data Attributes</h5>
                        <Form.Text className="text-muted mb-2">
                            Select the data attributes from the input file to map to the corresponding block attributes. Leave an entry unselected
                            if a source attribute is not available. Block name and crop type are required attributes.
                        </Form.Text>
                        <div className="border p-3 rounded-lg">
                            {Object.keys(mapping).map((k: string) => (
                                k === "cached_shp_acres" 
                                ? null 
                                : <div key={k} className="align-items-center d-flex row">
                                    <Form.Label column sm="4" className="text-capitalize">
                                        {k.replace('_', ' ')}
                                    </Form.Label>
                                    <Col sm="8">
                                        <Form.Control 
                                            as="select" 
                                            className="mt-1" 
                                            name={k} 
                                            size="sm"
                                            onChange={(e: any) => onMappingChanged(e)}
                                            value={mapping[k as keyof IMappings]}
                                        >
                                           
                                            {(k === "acres")
                                                ? <option style={{fontWeight:'bold'}} value="-99">Calculated from .shp</option>
                                                : <option value="-1">Select data source</option>
                                            }
                                            {getMappingOptions(features.features[0]).map((key: string) => (
                                                <option key={key} value={key}>{key}</option>
                                            ))}
                                        </Form.Control>
                                    </Col>
                                </div>
                            ))}
                        </div>
                    </div>
                }
            </div>

            {(fieldInputs && fieldInputs.length > 0) &&
                <div className="px-3 py-2">
                    <div className="row px-3">
                        <div className="col-sm-1" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}><strong>#</strong></div>
                        <div className="col" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}><strong>Block Name</strong></div>
                        <div className="col" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}><strong>Crop</strong></div>
                        <div className="col" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}><strong>Variety</strong></div>
                        <div className="col" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}><strong>Acres</strong></div>
                        <div className="col" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}><strong>Year Planted</strong></div>
                    </div>
                </div>
            }
            <CreateGrower isOpen={showCreateGrower} onClose={() => setShowCreateGrower(false)} />
            <CreateFarm isOpen={showCreateFarm} onClose={() => setShowCreateFarm(false)} />
            <div className="flex-grow-1 pt-0 px-3 py-2" style={{flexBasis: 0}}>
                <Formik
                    innerRef={ref}
                    validationSchema={schema}
                    enableReinitialize={true}
                    initialValues={{
                        inputs: fieldInputs,
                        grower_id: defaultValues.grower_id,
                        farm_id: defaultValues.farm_id,
                    }}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={async (values, { setSubmitting }) => {
                        try {
                            await doFieldImport(values.inputs);
                        } catch(error) {
                            console.log(error);
                        }
                    }}
                >
                    {({
                        values,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        setFieldValue,
                        isSubmitting,
                    }) => {
                        return (
                            <Form noValidate onSubmit={handleSubmit} autoComplete="off" className="d-flex flex-column h-100 justify-content-between">
                                <ListGroup>
                                    {values.inputs.map((f: FieldInput, index: number) => (
                                        console.log("071924 f", f),
                                        <ListGroup.Item
                                            key={f.id}
                                            as="li"
                                            className="px-3"
                                            action
                                            variant="light"
                                            style={{ cursor: 'pointer' }}
                                            onMouseEnter={() => onMouseEnter(f)}
                                            onMouseLeave={() => onMouseLeave(f)}>
                                            
                                            <div className="row d-flex align-items-center">
                                                <Col sm="1" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}>
                                                    {f.id}
                                                </Col>
                                                <Col style={{paddingLeft:'0px', paddingRight:'0px'}}>
                                                    <Form.Control
                                                        type="text"
                                                        name={`inputs[${index}].name`}
                                                        size="sm"
                                                        value={f.name}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="enter block name"
                                                    />
                                                </Col>
                                                <Col style={{paddingLeft:'0px', paddingRight:'0px'}}>
                                                    <Form.Control
                                                        as="select"
                                                        name={`inputs[${index}].crop_id`}
                                                        size="sm"
                                                        //value={f.crop_id}
                                                        value={
                                                            crops.filter((crop: any) => f.crop_id.toLocaleLowerCase() === crop.name.toLocaleLowerCase()).length 
                                                                ? crops.filter((crop: any) => f.crop_id.toLocaleLowerCase() === crop.name.toLocaleLowerCase())[0].id.toString() 
                                                                : crops.filter((crop: any) => f.crop_id.toLocaleLowerCase() === crop.id.toString().toLocaleLowerCase()).length
                                                                    ? crops.filter((crop: any) => f.crop_id.toLocaleLowerCase() === crop.id.toString().toLocaleLowerCase())[0].id.toString() 
                                                                    : ""
                                                        }
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}          
                                                    >
                                                        <option value="" disabled>- select -</option>
                                                        {crops.map((c: Crop) => (
                                                            <option key={c.id} value={c.id}>{c.name}</option>
                                                        ))}
                                                    </Form.Control>    
                                                </Col>
                                                <Col style={{paddingLeft:'0px', paddingRight:'0px'}}>
                                                    <Form.Control
                                                        as="select"
                                                        name={`inputs[${index}].variety`}
                                                        size="sm"
                                                        //value={f.variety}
                                                        value={
                                                            varieties.filter((variety: any) => f.variety.toLocaleLowerCase() === variety.name.toLocaleLowerCase()).length 
                                                                ? varieties.filter((variety: any) => f.variety.toLocaleLowerCase() === variety.name.toLocaleLowerCase())[0].id.toString() 
                                                                : varieties.filter((variety: any) => {
                                                                        console.log("071924 variety", variety);
                                                                        console.log("071924 f.variety", f.variety);
                                                                        return f.variety.toLocaleLowerCase() === variety.id.toString().toLocaleLowerCase()
                                                                    }).length
                                                                    ? varieties.filter((variety: any) => f.variety.toLocaleLowerCase() === variety.id.toString().toLocaleLowerCase())[0].id.toString() 
                                                                    : ""
                                                        }
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}          
                                                    >
                                                        <option value="" disabled>- select -</option>
                                                        {varieties.map((c: Variety) => (
                                                            <option key={c.id} value={c.id}>{c.name}</option>
                                                        ))}
                                                    </Form.Control>    
                                                </Col>
                                                <Col style={{paddingLeft:'0px', paddingRight:'0px'}}>
                                                    <Form.Control
                                                        type="text"
                                                        name={`inputs[${index}].acres`}
                                                        size="sm"
                                                        value={f.acres}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="enter acres value"
                                                    />
                                                </Col>
                                                <Col style={{paddingLeft:'0px', paddingRight:'0px'}}>
                                                    <Form.Control
                                                        type="text"
                                                        name={`inputs[${index}].year_planted`}
                                                        size="sm"
                                                        value={f.year_planted}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="enter year planted"
                                                    />
                                                </Col>
                                            </div>
                                            {
                                                hasAnyErrors(errors, index) ? (
                                                    <Alert variant="danger" className="mt-3">
                                                         <ul className="mb-0">
                                                        {
                                                            ['id', 'name', 'crop_id', 'variety', 'acres', 'year_planted', 'row_spacing', 'plant_spacing'].map((p:string) => {
                                                                return hasError(errors, index, p) ? (
                                                                    <li className="is-invalid " key={p}>
                                                                        { errorMessage(errors, index, p)}
                                                                    </li>
                                                                ) : null
                                                            }) 
                                                        }
                                                        </ul>
                                                    </Alert>
                                                ) : null
                                            }
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                                <Form.Group>
                                        <Form.Label className="d-flex justify-content-between">
                                            Grower
                                        </Form.Label>
                                        <div className="d-flex"> 
                                            <Form.Control
                                                as="select"
                                                name="grower_id"
                                                size="sm"
                                                value={values.grower_id}
                                                onBlur={handleBlur}
                                                isInvalid={!!errors.grower_id}
                                                onChange={e => {
                                                    console.log(e);
                                                    handleChange(e);
                                                    setFieldValue('farm_id', ""); // reset farm
                                                    setCurrentGrowerId(e.currentTarget.value ? parseInt(e.currentTarget.value) : null);
                                                    setGrowersFarms(e.currentTarget.value ? parseInt(e.currentTarget.value) : -1);
                                                }}
                                            >
                                                <option value="" label="Select a grower" disabled />
                                                {growers.map(option => {
                                                    return <option key={option.id} value={option.id}>{option.name}</option>
                                                })}
                                            </Form.Control>
                                            <Button size="sm" className="ml-2" title="Create" onClick={() => setShowCreateGrower(true)}>
                                                +
                                            </Button>
                                        </div>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.grower_id}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group>
                                        <Form.Label className="d-flex justify-content-between">
                                            Farm
                                        </Form.Label>
                                        <div className="d-flex">
                                            <Form.Control
                                                as="select"
                                                name="farm_id"
                                                size="sm"
                                                value={values.farm_id}
                                                onChange={e => {
                                                    console.log(e);
                                                    handleChange(e);
                                                    setCurrentGrowerFarm(e.currentTarget.value ? parseInt(e.currentTarget.value) : null)
                                                }}
                                                onBlur={handleBlur}
                                                isInvalid={!!errors.farm_id}
                                            >
                                                <option value="" label="Select a farm" disabled />
                                                {currentGrowerFarms.map(option => {
                                                    return <option key={option.id} value={option.id}>{option.name}</option>
                                                })}
                                            </Form.Control>
                                            <Button size="sm" className="ml-2" title="Create" onClick={() =>  setShowCreateFarm(true)}>
                                                +
                                            </Button>
                                        </div>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.farm_id}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                <div>
                                    <div className="d-flex justify-content-between my-2">
                                        <div className="d-flex gap-2">
                                            <Button type="submit" variant="primary" size="sm" disabled={fieldInputs.length === 0 || isSubmitting}>
                                                Import
                                            </Button>
                                            <Button variant="secondary" size="sm" className="mx-2" onClick={() => {
                                                history.push(`${match.url}`);
                                            }}>
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
}

export default memo(ImportForm);
